import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Euro, Users, Settings, Save, RefreshCw, Clock, 
  Edit2, Check, X, AlertTriangle, Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

export default function HourlyRatesPage() {
  const { user, isAssistant } = useOutletContext() || {};
  const [defaultRate, setDefaultRate] = useState(35);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingDefault, setEditingDefault] = useState(false);
  const [tempDefaultRate, setTempDefaultRate] = useState(35);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [tempEmployeeRate, setTempEmployeeRate] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('client_token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/pointage/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDefaultRate(data.default_rate);
        setTempDefaultRate(data.default_rate);
        setEmployees(data.employees);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const saveDefaultRate = async () => {
    const token = localStorage.getItem('client_token');
    setSaving(true);

    try {
      const response = await fetch(
        `${API_URL}/api/pointage/hourly-rates/default?rate=${tempDefaultRate}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        setDefaultRate(tempDefaultRate);
        setEditingDefault(false);
        toast.success('Taux par défaut mis à jour');
        // Update employees who don't have custom rate
        setEmployees(prev => prev.map(emp => ({
          ...emp,
          effective_rate: emp.custom_rate || tempDefaultRate
        })));
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const saveEmployeeRate = async (userId) => {
    const token = localStorage.getItem('client_token');
    setSaving(true);

    try {
      const response = await fetch(
        `${API_URL}/api/pointage/hourly-rates/employee/${userId}?rate=${tempEmployeeRate}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        setEmployees(prev => prev.map(emp => 
          emp.user_id === userId 
            ? { ...emp, custom_rate: tempEmployeeRate, effective_rate: tempEmployeeRate }
            : emp
        ));
        setEditingEmployee(null);
        toast.success('Taux employé mis à jour');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const removeCustomRate = async (userId) => {
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(
        `${API_URL}/api/pointage/hourly-rates/employee/${userId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        setEmployees(prev => prev.map(emp => 
          emp.user_id === userId 
            ? { ...emp, custom_rate: null, effective_rate: defaultRate }
            : emp
        ));
        toast.success('Taux personnalisé supprimé');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  // Calculate totals
  const totalHours = employees.reduce((sum, emp) => sum + (emp.total_hours || 0), 0);
  const totalCost = employees.reduce((sum, emp) => sum + ((emp.total_hours || 0) * emp.effective_rate), 0);

  return (
    <div className="space-y-6" data-testid="hourly-rates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
            <Euro className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Grille tarifaire</p>
            <p className="text-sm text-slate-500 mt-0.5">Configurez les taux pour le calcul de rentabilité</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="text-sm text-blue-800">
            Les taux horaires sont utilisés pour calculer le coût de la main d'œuvre 
            dans le tableau de bord de rentabilité. Vous pouvez définir un taux par défaut 
            pour tous les employés, puis personnaliser le taux de certains employés si nécessaire.
          </p>
        </div>
      </div>

      {/* Default Rate Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-rm-primary/10 rounded-full flex items-center justify-center">
              <Settings className="w-6 h-6 text-rm-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Taux horaire par défaut</p>
              <p className="text-sm text-slate-500 mt-0.5">Appliqué à tous les employés sans taux personnalisé</p>
            </div>
          </div>
          
          {editingDefault ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={tempDefaultRate}
                onChange={(e) => setTempDefaultRate(parseFloat(e.target.value) || 0)}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-lg font-bold text-center"
                min="0"
                max="500"
                step="0.5"
              />
              <span className="text-slate-500">€/h</span>
              <Button 
                size="sm" 
                onClick={saveDefaultRate}
                disabled={saving}
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setEditingDefault(false);
                  setTempDefaultRate(defaultRate);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-rm-primary">{defaultRate}€</span>
              <span className="text-slate-500">/heure</span>
              {!isAssistant && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setEditingDefault(true)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-2">
            <Users className="w-4 h-4" />
            <span className="text-xs">Employés avec pointages</span>
          </div>
          <p className="text-2xl font-bold text-rm-text">{employees.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-2">
            <Clock className="w-4 h-4" />
            <span className="text-xs">Total heures</span>
          </div>
          <p className="text-2xl font-bold text-rm-text">{Math.round(totalHours)}h</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-2">
            <Euro className="w-4 h-4" />
            <span className="text-xs">Coût main d'œuvre</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(totalCost)}</p>
        </div>
      </div>

      {/* Employees List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <Users className="w-4 h-4" />
            Taux par employé
          </p>
        </div>

        {employees.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {employees.map((employee) => (
              <motion.div
                key={employee.user_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <span className="font-medium text-slate-600">
                        {employee.user_name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-rm-text">{employee.user_name}</p>
                      <p className="text-xs text-slate-500">
                        {employee.total_hours}h sur {employee.chantiers || 0} chantier(s)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Calculated cost */}
                    <div className="text-right mr-4">
                      <p className="text-xs text-slate-500">Coût estimé</p>
                      <p className="font-medium text-red-600">
                        {formatCurrency((employee.total_hours || 0) * employee.effective_rate)}
                      </p>
                    </div>

                    {editingEmployee === employee.user_id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={tempEmployeeRate}
                          onChange={(e) => setTempEmployeeRate(parseFloat(e.target.value) || 0)}
                          className="w-20 px-2 py-1.5 border border-slate-200 rounded text-center"
                          min="0"
                          max="500"
                          step="0.5"
                        />
                        <span className="text-sm text-slate-500">€/h</span>
                        <Button 
                          size="sm" 
                          onClick={() => saveEmployeeRate(employee.user_id)}
                          disabled={saving}
                        >
                          {saving ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setEditingEmployee(null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span className="text-xl font-bold text-rm-text">
                              {employee.effective_rate}€
                            </span>
                            <span className="text-slate-500">/h</span>
                          </div>
                          {employee.custom_rate ? (
                            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                              Personnalisé
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">
                              Par défaut
                            </span>
                          )}
                        </div>
                        {!isAssistant && (
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setEditingEmployee(employee.user_id);
                                setTempEmployeeRate(employee.effective_rate);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {employee.custom_rate && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removeCustomRate(employee.user_id)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-slate-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Aucun employé avec des pointages</p>
            <p className="text-sm mt-1">Les employés apparaîtront ici après leur premier pointage</p>
          </div>
        )}
      </div>
    </div>
  );
}
